import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ClipboardIcon, CheckIcon, DocumentTextIcon } from '@heroicons/vue/solid';
import { NumberService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import { NumberModel, WhatsappCodeModel } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { moment } from '@/utils/datetime';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';

const perPage = 10;
const latestCodeVisibleSeconds = 180;


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsappCodeMonitorPage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const numberId = parseInt(route.params.id as string, 10);
const number = ref<NumberModel | null>(null);
const codes = ref<WhatsappCodeModel[]>([]);
const latestCode = ref<WhatsappCodeModel | null>(null);

const count = ref(0);
const currentPage = ref(1);
const copied = ref(false);
const newlyReceivedId = ref<number | null>(null);
let pollingInterval: number | null = null;

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const digitChars = computed(() => {
    if (!latestCode.value || !latestCode.value.code) {
        return Array(6).fill('-');
    }
    const codeStr = latestCode.value.code.toString();
    const chars = codeStr.split('');
    while (chars.length < 6) {
        chars.push('-');
    }
    return chars;
});

const formatTime = (dateStr: string): string => {
    return moment(dateStr).format('DD/MM/YYYY HH:mm:ss');
};

const isRecentlyReceived = (code: WhatsappCodeModel): boolean => {
    const receivedAt = moment(code.receivedAt);
    if (!receivedAt.isValid()) return false;

    const secondsSinceReceived = moment().diff(receivedAt, 'seconds');
    return secondsSinceReceived >= 0 && secondsSinceReceived <= latestCodeVisibleSeconds;
};

const setLatestCode = (code?: WhatsappCodeModel) => {
    latestCode.value = code && isRecentlyReceived(code) ? code : null;
};

const copyToClipboard = async () => {
    if (!latestCode.value) return;
    try {
        await navigator.clipboard.writeText(latestCode.value.code);
        copied.value = true;
        toast.success(t('whatsappCode.toast.copiedSuccess'));
        setTimeout(() => {
            copied.value = false;
        }, 1500);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy: ', err);
    }
};

const fetchNumber = async () => {
    try {
        const { data } = await NumberService.get(numberId);
        number.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchCodes = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
        const { data, headers } = await WhatsappCodeService.getCodes(numberId, {
            params: {
                page: currentPage.value,
                per_page: perPage,
            },
        });
        codes.value = data;
        count.value = parseInt(headers['x-pagination-count']) || data.length;

        if (data.length > 0 && currentPage.value === 1) {
            setLatestCode(data[0]);
        } else if (!latestCode.value && data.length > 0) {
            // fallback if we are on page 2+ and didn't load first page yet
            const firstPageRes = await WhatsappCodeService.getCodes(numberId, {
                params: { page: 1, per_page: 1 },
            });
            if (firstPageRes.data.length > 0) {
                setLatestCode(firstPageRes.data[0]);
            }
        } else if (data.length === 0 && currentPage.value === 1) {
            setLatestCode();
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        if (showLoading) setLoading(false);
    }
};

const startPolling = () => {
    pollingInterval = window.setInterval(async () => {
        try {
            // Check for new codes on page 1
            const { data } = await WhatsappCodeService.getCodes(numberId, {
                params: {
                    page: 1,
                    per_page: 1,
                },
            });

            if (data && data.length > 0) {
                const newest = data[0];
                const previousLatestId = latestCode.value?.id || null;
                setLatestCode(newest);
                const isNew = Boolean(latestCode.value && newest.id !== previousLatestId);

                if (isNew) {
                    if (currentPage.value === 1) {
                        // Prepend if not already in current codes list
                        if (!codes.value.some((c) => c.id === newest.id)) {
                            codes.value.unshift(newest);
                            if (codes.value.length > perPage) {
                                codes.value.pop();
                            }
                            // Trigger highlight animation
                            newlyReceivedId.value = newest.id;
                            setTimeout(() => {
                                newlyReceivedId.value = null;
                            }, 2500);
                        }
                    } else {
                        // If user is on page 2+, just refresh current page
                        await fetchCodes(false);
                    }
                }
            } else {
                setLatestCode();
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Polling error:', err);
        }
    }, 5000);
};

const handlePageChange = (page: number) => {
    currentPage.value = page;
    fetchCodes(true);
};

onMounted(async () => {
    await fetchNumber();
    await fetchCodes(true);
    startPolling();
});

onUnmounted(() => {
    if (pollingInterval) {
        window.clearInterval(pollingInterval);
    }
});

const __returned__ = { route, t, uiStore, numberId, number, codes, latestCode, count, perPage, currentPage, latestCodeVisibleSeconds, copied, newlyReceivedId, get pollingInterval() { return pollingInterval }, set pollingInterval(v) { pollingInterval = v }, setLoading, digitChars, formatTime, isRecentlyReceived, setLatestCode, copyToClipboard, fetchNumber, fetchCodes, startPolling, handlePageChange, get ClipboardIcon() { return ClipboardIcon }, get CheckIcon() { return CheckIcon }, get DocumentTextIcon() { return DocumentTextIcon }, get applyPhoneMask() { return applyPhoneMask }, SVGWhatsApp }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})